/**
 * "Pay now" panel on an invoice. Shows the customer-facing link (or the UPI
 * QR) so staff can copy it, scan it at the counter, or push it to WhatsApp.
 *
 * Renders nothing at all when there is no gateway connected or no balance
 * outstanding — an invoice that is settled should not be inviting payment.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Check, ExternalLink, Info, Loader2 } from 'lucide-react';
import { API } from '../../../lib/api';

export const PayLinkPanel = ({ invoiceId, refreshKey }) => {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/payments/invoice/${invoiceId}/pay-link`);
      setInfo(data);
    } catch {
      setInfo(null);
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) {
    return <Loader2 className="animate-spin text-slate-300" size={14} />;
  }
  if (!info || !info.connected || info.balance <= 0) return null;
  const target = info.pay_url || info.upi_uri;
  if (!target) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(info.pay_url || info.upi_vpa);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked */ }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4" data-testid="pay-link-panel">
      <div className="flex items-start gap-4">
        <div className="shrink-0 p-2 bg-white border border-slate-200 rounded-lg">
          <QRCodeSVG value={target} size={92} level="M" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-900">
            {info.pay_url ? 'Customer pay link' : 'UPI QR'}
          </h3>
          {info.pay_url ? (
            <>
              <div className="flex items-center gap-2 mt-1.5">
                <code className="text-[11px] text-slate-600 truncate" data-testid="pay-link-url">{info.pay_url}</code>
                <button onClick={copy} className="shrink-0 text-slate-400 hover:text-teal-600" data-testid="copy-pay-link-btn">
                  {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                </button>
                <a
                  href={info.pay_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 text-slate-400 hover:text-teal-600"
                  aria-label="Open the payment page"
                  title="Open the payment page"
                  data-testid="open-pay-link-btn"
                >
                  <ExternalLink size={13} />
                </a>
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">
                Already on the invoice PDF, in the email and in the WhatsApp message.
                The invoice marks itself paid once the payment clears.
              </p>
            </>
          ) : (
            <>
              <div className="font-mono text-xs text-teal-700 mt-1.5" data-testid="pay-link-vpa">{info.upi_vpa}</div>
              <div className="flex gap-1.5 text-[11px] text-amber-800 mt-2">
                <Info size={13} className="shrink-0 mt-0.5" />
                <span>
                  A UPI QR cannot confirm payment — once the money lands, record it
                  with the UTR using "Record payment".
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default PayLinkPanel;
